import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, SectionTitle } from "@/components/Kpi";
import { api } from "@/lib/api";
import { num } from "@/lib/format";

const ETICHETTE = { audit_logs: "Audit log", login_attempts: "Tentativi di accesso" };

/** Spazio occupato dal database e pulizia dei log tecnici. */
export default function ManutenzioneDb() {
  const [dati, setDati] = useState(null);
  const [mesi, setMesi] = useState({});
  const [inCorso, setInCorso] = useState("");

  const carica = () => api.get("/manutenzione/spazio").then((r) => {
    setDati(r.data);
    setMesi(r.data.conservazione || {});
  }).catch(() => setDati({ voci: [], totale_mb: 0 }));
  useEffect(() => { carica(); }, []);

  const salva = async () => {
    setInCorso("salva");
    try {
      await api.put("/manutenzione/conservazione", mesi);
      toast.success("Conservazione aggiornata");
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setInCorso(""); }
  };

  const pulisci = async (simulazione) => {
    setInCorso(simulazione ? "simula" : "pulisci");
    try {
      const r = await api.post(`/manutenzione/pulizia?simulazione=${simulazione}`);
      const righe = r.data.esito.map((e) => `${e.label}: ${e.eliminate}`).join(" · ");
      toast.success(simulazione
        ? `Da eliminare — ${righe}`
        : `Eliminate ${r.data.totale} righe — ${righe}`);
      if (!simulazione) carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pulizia non riuscita");
    } finally { setInCorso(""); }
  };

  if (!dati) return null;
  return (
    <div className="mt-10" data-testid="manutenzione-db">
      <SectionTitle sub="Spazio occupato e conservazione dei log tecnici. I dati di business non vengono mai cancellati.">
        Manutenzione database
      </SectionTitle>
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <Card className="p-6 overflow-x-auto">
          <div className="text-sm text-slate-500">
            Totale occupato: <strong className="text-slate-900">{num(dati.totale_mb, 2)} MB</strong>
          </div>
          <table className="mt-3 w-full text-sm" data-testid="tabella-spazio">
            <thead>
              <tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                <th className="text-left px-4 py-2">Tabella</th>
                <th className="text-right px-4 py-2">Righe</th>
                <th className="text-right px-4 py-2">Dati</th>
                <th className="text-right px-4 py-2">Indici</th>
              </tr>
            </thead>
            <tbody>
              {dati.voci.map((v) => (
                <tr key={v.collection} className="border-t border-slate-100"
                  data-testid={`spazio-${v.collection}`}>
                  <td className="px-4 py-1.5 font-medium text-slate-800">{v.collection}</td>
                  <td className="px-4 py-1.5 text-right text-slate-600">{num(v.righe, 0)}</td>
                  <td className="px-4 py-1.5 text-right text-slate-800">{num(v.dati_mb, 2)} MB</td>
                  <td className="px-4 py-1.5 text-right text-slate-500">{num(v.indici_mb, 2)} MB</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card className="h-fit p-6">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Conservazione</div>
          <div className="mt-3 space-y-3">
            {Object.keys(ETICHETTE).map((k) => (
              <label key={k} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-700">{ETICHETTE[k]}</span>
                <span className="flex items-center gap-2">
                  <input type="number" min="1" max="120" value={mesi[k] ?? ""}
                    data-testid={`conservazione-${k}`}
                    onChange={(e) => setMesi((s) => ({ ...s, [k]: e.target.value }))}
                    className="w-20 rounded border border-slate-200 px-2 py-1 text-right" />
                  <span className="text-xs text-slate-500">mesi</span>
                </span>
              </label>
            ))}
          </div>
          <button type="button" onClick={salva} disabled={inCorso === "salva"}
            data-testid="btn-salva-conservazione"
            className="mt-4 w-full rounded-full bg-[#14284B] px-4 py-2 text-sm font-bold text-white
              disabled:opacity-50">
            Salva
          </button>
          <div className="mt-4 flex flex-col gap-2">
            <button type="button" onClick={() => pulisci(true)} disabled={inCorso === "simula"}
              data-testid="btn-simula-pulizia"
              className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold
                text-slate-600 hover:border-slate-400 hover:text-slate-900 disabled:opacity-50">
              Mostra cosa verrebbe eliminato
            </button>
            <button type="button" disabled={inCorso === "pulisci"}
              data-testid="btn-pulizia"
              onClick={() => window.confirm("Eliminare definitivamente i log oltre la conservazione impostata?")
                && pulisci(false)}
              className="rounded-full border border-red-200 px-4 py-2 text-xs font-semibold
                text-red-600 hover:bg-red-50 disabled:opacity-50">
              Esegui pulizia ora
            </button>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
            La pulizia gira da sola il 3 di ogni mese. Riguarda solo i log tecnici: pratiche,
            budget, utenti e newsletter non vengono mai toccati.
          </p>
        </Card>
      </div>
    </div>
  );
}
